import { ACTIONS } from './ActionPattern';

/**
 * MENU PERMISSION SYSTEM
 *
 * This configuration defines the main menu structure with comprehensive permission controls.
 * The permission system supports two types of checks that work together:
 *
 * 1. SCOPE-BASED PERMISSIONS (scopes)
 *    - Uses user.scopes array from userStore
 *    - Example scopes: 'admin', 'hr', 'super-admin', 'lead-dispatcher', 'driver-coordinator'
 *    - If scopes array is defined, user must have AT LEAST ONE matching scope
 *    - If scopes array is empty/undefined, the menu item is visible to all users
 *    - super-admin is treated as a regular scope (must be explicitly listed to have access)
 *
 * 2. ACTION-BASED PERMISSIONS (actionPermission)
 *    - Uses permissionStore.isDenied() to check against API endpoint patterns
 *    - Direct reference to ACTIONS constants (e.g., ACTIONS.VIEW_DASHBOARD)
 *    - Checks if user has permission to access specific API endpoints
 *    - If action is denied, menu item is hidden
 *
 * PERMISSION LOGIC FLOW:
 *
 * For Parent Menus:
 *   1. Parent menus DO NOT have their own permissions (no scopes or actionPermission)
 *   2. Filter submenus by their permissions
 *   3. Parent is visible ONLY if at least one submenu is visible
 *   4. Parent link will be the first visible submenu link
 *
 * For Submenus:
 *   1. Check submenu's scopes (if defined)
 *   2. Check submenu's actionPermission (if defined)
 *   3. Submenu is visible only if both checks pass
 *
 */

export const MENUS = [
  {
    label: 'Dispatch',
    subMenus: [
      {
        link: '/routes',
        label: 'Dispatch',
      },
      {
        link: '/search',
        label: 'Search',
      },
      {
        link: '/frankenroute',
        label: 'Frankenroute',
        scopes: ['lead-dispatcher', 'admin'],
      },
      {
        link: '/dashboard',
        label: 'Dashboard',
        actionPermission: ACTIONS.VIEW_DASHBOARD,
      },
      {
        link: process.env.REACT_APP_REDELIVERY_URL,
        label: 'Redelivery',
      },
      {
        link: process.env.REACT_APP_GEOCODER_URL,
        label: 'Address Tool',
        scopes: ['super-admin'],
      },
    ],
  },
  {
    label: 'Booking',
    subMenus: [
      {
        link: '/ticket-booking',
        label: 'Ticket Booking',
      },
      {
        link: '/schedule',
        label: 'Direct Booking',
      },
      {
        link: '/region-settings',
        label: 'Region Settings',
        actionPermission: ACTIONS.REGION_SETTINGS,
      },
    ],
  },
  {
    label: 'Linehaul',
    subMenus: [
      {
        link: '/linehauls',
        label: 'Linehaul',
      },
      {
        link: '/linehauls/summary',
        label: 'Summary',
      },
      {
        link: '/linehauls/report',
        label: 'Report',
      },
      {
        link: '/linehauls/overview',
        label: 'Overview',
      },
    ],
  },
  {
    label: 'Drivers',
    subMenus: [
      {
        link: '/drivers',
        label: 'Drivers',
        scopes: ['dispatcher', 'admin', 'hr'],
      },
      {
        link: '/driver-crews',
        label: 'Driver Crews',
      },
      {
        link: '/driver-pools',
        label: 'Driver Pools',
      },
      {
        link: '/driver-incentives',
        label: 'Incentives',
      },
      {
        link: '/driver-probations',
        label: 'Driver Probations',
        scopes: ['admin', 'super-admin', 'driver-coordinator'],
      },
      {
        link: '/driver-rating-config',
        label: 'Driver Rating Config',
        scopes: ['super-admin'],
      },
    ],
  },
  {
    label: 'Comms',
    subMenus: [
      {
        link: '/messenger',
        label: 'Messenger',
      },
      {
        link: '/driver-announcements',
        label: 'Announcement',
      },
    ],
  },
];
